import { useState, useEffect, useCallback, useRef } from 'react'

const INTRO_KEY = 'ws_brochure_intro_seen'

interface IntroSentence {
  text: string
  highlight: string
}

const introSentences: IntroSentence[] = [
  { text: 'The ', highlight: 'Adviser Operating System' },
  { text: 'More ', highlight: 'profit' },
  { text: 'Less ', highlight: 'admin' },
  { text: 'Work you ', highlight: 'enjoy' },
  { text: "Integrates with ", highlight: "what you've got" },
  { text: 'Or becomes ', highlight: 'everything you need' },
  { text: 'Built by advisers. ', highlight: 'For advisers' },
  { text: 'The future of advice ', highlight: 'starts here' },
  { text: 'Welcome to ', highlight: 'WealthSpace' },
]

function isInternalNavigation(): boolean {
  if (typeof window === 'undefined') return false
  const referrer = document.referrer
  if (!referrer) return false
  try {
    const referrerUrl = new URL(referrer)
    return referrerUrl.origin === window.location.origin
  } catch {
    return false
  }
}

function shouldShowIntro(): boolean {
  if (typeof window === 'undefined') return false
  // Skip intro if already seen OR if navigating from another page on the site
  if (sessionStorage.getItem(INTRO_KEY) || isInternalNavigation()) {
    sessionStorage.setItem(INTRO_KEY, 'true')
    return false
  }
  return true
}

export default function IntroAnimation() {
  // Use lazy initial state to determine if intro should be visible
  const [isVisible, setIsVisible] = useState(() => shouldShowIntro())
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [textState, setTextState] = useState<'visible' | 'fading' | 'hidden'>('hidden')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const skipIntro = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsFadingOut(true)
    sessionStorage.setItem(INTRO_KEY, 'true')
    setTimeout(() => {
      setIsVisible(false)
    }, 800)
  }, [])

  useEffect(() => {
    // If not visible, nothing to do
    if (!isVisible) return

    const showNextSentence = () => {
      if (currentIndex >= introSentences.length) {
        setIsFadingOut(true)
        sessionStorage.setItem(INTRO_KEY, 'true')
        setTimeout(() => {
          setIsVisible(false)
        }, 800)
        return
      }

      // Start fading out current text
      setTextState('fading')

      // After fade out, show new text
      timeoutRef.current = setTimeout(() => {
        setTextState('visible')
        setCurrentIndex((prev) => prev + 1)

        // Schedule next sentence
        timeoutRef.current = setTimeout(showNextSentence, 2000)
      }, 400)
    }

    // Initial delay before starting
    timeoutRef.current = setTimeout(showNextSentence, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isVisible) return null

  const currentSentence = introSentences[Math.max(0, currentIndex - 1)]

  return (
    <div
      className={`
        fixed inset-0 bg-[#0a0d12] flex items-center justify-center z-[9999] flex-col
        transition-opacity duration-[800ms] ease-out
        ${isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100'}
      `}
    >
      {/* Intro Text */}
      <div
        className={`
          font-serif text-4xl text-brand-light text-center max-w-[700px] px-6 leading-[1.4]
          transition-all duration-[800ms] ease-out
          ${textState === 'visible' ? 'opacity-100 translate-y-0' : ''}
          ${textState === 'fading' ? 'opacity-0 -translate-y-5' : ''}
          ${textState === 'hidden' ? 'opacity-0 translate-y-5' : ''}
        `}
      >
        {currentIndex > 0 && currentSentence && (
          <>
            {currentSentence.text}
            <span className="whitespace-nowrap inline" style={{ color: '#14b8a6' }}>
              {currentSentence.highlight}
            </span>
            .
          </>
        )}
      </div>

      {/* Progress Dots */}
      <div className="absolute bottom-[60px] flex gap-2">
        {introSentences.map((_, index) => (
          <div
            key={index}
            className="w-2 h-2 rounded-full transition-all duration-300 ease-out"
            style={{
              background: index === currentIndex - 1 || (currentIndex === 0 && index === 0)
                ? '#14b8a6'
                : 'rgba(255,255,255,0.2)',
              boxShadow: index === currentIndex - 1 || (currentIndex === 0 && index === 0)
                ? '0 0 12px rgba(20,184,166,0.5)'
                : 'none'
            }}
          />
        ))}
      </div>

      {/* Skip Button */}
      <button
        onClick={skipIntro}
        className="
          absolute bottom-[100px] bg-transparent border border-white/20 text-brand-muted
          px-6 py-2.5 rounded-md text-sm font-sans cursor-pointer
          transition-all duration-300 ease-out
          hover:border-white/40 hover:text-brand-light hover:bg-white/5
        "
      >
        Skip
      </button>
    </div>
  )
}
